// Edit shift / set day off, in the panel (Cal-09). Half-hour steps; the end
// must come after the start.
//
// It says plainly what a change leaves uncovered — "Ana R. still has 3
// sessions outside these hours" — and moves nothing: whether a shift change
// should reassign booked work is not a recorded rule (never-invent-policy.md).
//
// ⚠ In the demo, Save changes the screen only (calendar.mock-edits.ts). The
// real save will go through the Schedules page's functions and roles (B5).

import { Button } from "@bednbody/ui/components/button";
import { Label } from "@bednbody/ui/components/label";
import { NativeSelect, NativeSelectOption } from "@bednbody/ui/components/native-select";
import { Switch } from "@bednbody/ui/components/switch";
import { WarningIcon } from "@phosphor-icons/react";
import { useState } from "react";
import { type BoardRow, blocksOutsideShift, clockLabel, type MinuteSpan } from "./calendar-board";
import { shiftTimeOptions } from "./calendar.mock-edits";

export function CalendarShiftEditor({
  row,
  onSave,
  onCancel,
}: {
  row: BoardRow;
  onSave: (shift: MinuteSpan | null) => void;
  onCancel: () => void;
}) {
  const [start, setStart] = useState(row.shift?.startMin ?? 14 * 60);
  const [end, setEnd] = useState(row.shift?.endMin ?? 22 * 60);
  const [dayOff, setDayOff] = useState(row.shift === null);
  const options = shiftTimeOptions();
  const invalid = !dayOff && end <= start;
  const next = dayOff ? null : { startMin: start, endMin: end };
  const uncovered = invalid ? [] : blocksOutsideShift(row, next);
  const work = row.role === "rider" ? "trip" : "session";

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-2">
        {(
          [
            ["calendar-shift-start", "Starts", start, setStart],
            ["calendar-shift-end", "Ends", end, setEnd],
          ] as const
        ).map(([id, label, value, setValue]) => (
          <div className="space-y-1" key={id}>
            <Label htmlFor={id}>{label}</Label>
            <NativeSelect
              aria-invalid={invalid && label === "Ends"}
              className="w-full"
              disabled={dayOff}
              id={id}
              onChange={(event) => setValue(Number(event.target.value))}
              value={String(value)}
            >
              {options.map((minutes) => (
                <NativeSelectOption key={minutes} value={String(minutes)}>
                  {clockLabel(minutes)}
                  {minutes >= 24 * 60 ? " (next day)" : ""}
                </NativeSelectOption>
              ))}
            </NativeSelect>
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between rounded-lg bg-muted/40 px-3 py-2.5">
        <Label htmlFor="calendar-shift-off">Day off</Label>
        <Switch
          aria-label="Day off"
          checked={dayOff}
          id="calendar-shift-off"
          onCheckedChange={setDayOff}
        />
      </div>
      {invalid ? (
        <p className="text-sm text-destructive">The shift has to end after it starts.</p>
      ) : null}
      {uncovered.length > 0 ? (
        <p className="flex gap-2 rounded-lg bg-warning/10 px-3 py-2 text-sm text-warning">
          <WarningIcon className="mt-0.5 size-4 shrink-0" weight="fill" />
          <span>
            {row.name} still has {uncovered.length} {work}
            {uncovered.length === 1 ? "" : "s"} {dayOff ? "that day" : "outside these hours"} (
            {uncovered.map((block) => block.ref).join(", ")}). Changing the shift does not move
            them.
          </span>
        </p>
      ) : null}
      <p className="text-xs text-muted-foreground">
        Will use the same rules and roles as the Schedules page. In this demo it changes the screen
        only.
      </p>
      <div className="flex gap-2">
        <Button className="flex-1" disabled={invalid} onClick={() => onSave(next)}>
          Save shift
        </Button>
        <Button onClick={onCancel} variant="outline">
          Cancel
        </Button>
      </div>
    </div>
  );
}
